import React, { useState, useEffect, useMemo } from 'react';
import api, { formsApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, GripVertical, Eye, EyeOff, Pencil, Save, X,
  Loader2, ArrowUp, ArrowDown, FileText, Layout, Globe, Building2
} from 'lucide-react';
import { BUILTIN_TAB_FIELD_REGISTRY } from '../../lib/builtinTabFields';
import { TAB_ICONS as ICONS, IconByName } from '../../lib/tabIcons';

const BUILTIN_TABS = [
  { key: 'overview', name: 'Overview', icon: 'Layout' },
  { key: 'hospital', name: 'Hospital', icon: 'Hospital' },
  { key: 'concern', name: 'Chief Concern', icon: 'Activity' },
  { key: 'caregiver', name: 'Caregiver', icon: 'UserCog' },
  { key: 'pharmacy', name: 'Pharmacy', icon: 'Pill' },
  { key: 'clinical', name: 'Clinical', icon: 'Activity' },
  { key: 'files', name: 'Files', icon: 'FileText' },
];

const FIELD_TYPES = [
  { v: 'section', label: '— Section Header —' },
  { v: 'text', label: 'Text' },
  { v: 'textarea', label: 'Long Text (textarea)' },
  { v: 'number', label: 'Number' },
  { v: 'date', label: 'Date' },
  { v: 'select', label: 'Select (dropdown)' },
  { v: 'checkbox', label: 'Checkbox' },
  { v: 'email', label: 'Email' },
  { v: 'phone', label: 'Phone' },
  { v: 'file', label: 'File upload' },
];

// Icon library available for both tab icons AND per-field icons — shared via /lib/tabIcons.js
export { ICONS };

function IconFor({ name, className = 'w-4 h-4' }) {
  const C = ICONS[name] || FileText;
  return <C className={className} />;
}

export default function PatientTabsAdminPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [tabs, setTabs] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTab, setEditingTab] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, fRes] = await Promise.all([
        api.get('/patient-tabs'),
        formsApi.list().catch(() => ({ data: { forms: [] } })),
      ]);
      setTabs(tRes.data.tabs || []);
      const allForms = Array.isArray(fRes.data) ? fRes.data : (fRes.data.forms || []);
      setForms(allForms.filter(f => f.status === 'published'));
    } catch (e) {
      toast.error('Failed to load tabs');
    } finally {
      setLoading(false);
    }
  };

  const createTab = async (payload) => {
    try {
      const res = await api.post('/patient-tabs', payload);
      setTabs(prev => [...prev, res.data].sort((a, b) => a.order - b.order));
      toast.success('Tab created');
      setShowCreate(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create tab');
    }
  };

  const updateTab = async (id, updates) => {
    try {
      const res = await api.put(`/patient-tabs/${id}`, updates);
      setTabs(prev => prev.map(t => t.id === id ? res.data : t));
      toast.success('Tab updated');
      setEditingTab(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const deleteTab = async (id) => {
    if (!window.confirm('Delete this tab and all patient data saved on it? This cannot be undone.')) return;
    try {
      await api.delete(`/patient-tabs/${id}`);
      setTabs(prev => prev.filter(t => t.id !== id));
      toast.success('Tab deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const moveTab = async (id, direction) => {
    const idx = tabs.findIndex(t => t.id === id);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= tabs.length) return;
    const reordered = [...tabs];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setTabs(reordered);
    try {
      await api.post('/patient-tabs/reorder', { order: reordered.map(t => t.id) });
    } catch {
      load();
    }
  };

  // Built-in tabs that already have an override
  const overriddenKeys = useMemo(() => new Set(tabs.filter(t => t.type === 'builtin_override').map(t => t.builtin_key)), [tabs]);
  const addableBuiltins = BUILTIN_TABS.filter(b => !overriddenKeys.has(b.key));

  if (loading) {
    return <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="patient-tabs-admin">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342] flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Layout className="w-8 h-8 text-[#0E7490]" />
            Patient Record Tabs
          </h1>
          <p className="text-[#64748B] mt-1 text-sm">
            {isSuperAdmin
              ? 'Add or customize tabs shown on every patient record platform-wide. Practice admins can also add org-scoped tabs.'
              : 'Add tabs shown on every patient record in your organization.'}
          </p>
        </div>
        <Button className="careiq-btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)} data-testid="add-tab-btn">
          <Plus className="w-4 h-4" />
          Add Tab
        </Button>
      </div>

      {/* Built-in tabs (read-only + override-add) */}
      <Card className="careiq-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Built-in Tabs</CardTitle>
          <p className="text-xs text-[#64748B]">Default tabs on every patient record. You can hide, rename, or append extra fields via an override.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {BUILTIN_TABS.map(b => {
            const override = tabs.find(t => t.type === 'builtin_override' && t.builtin_key === b.key);
            return (
              <div key={b.key} className="flex items-center justify-between p-3 rounded border border-[#E2E8F0] bg-[#FAFBFC]" data-testid={`builtin-${b.key}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <IconFor name={b.icon} className="w-4 h-4 text-[#0E7490]" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0A2342]">
                      {override?.name || b.name}
                      {override?.name && override.name !== b.name && <span className="text-[10px] text-[#64748B] ml-2">(renamed from "{b.name}")</span>}
                    </div>
                    <div className="text-[11px] text-[#64748B] flex items-center gap-2">
                      Built-in
                      {override?.hidden && <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] rounded">Hidden</span>}
                      {override && <span className="px-1.5 py-0.5 bg-[#E0E7FF] text-[#3730A3] rounded">{override.fields?.length || 0} extra field{(override.fields?.length || 0) === 1 ? '' : 's'}</span>}
                      {override && <span className="px-1.5 py-0.5 bg-[#F0FDFA] text-[#0E7490] rounded flex items-center gap-1">
                        {override.scope === 'platform' ? <><Globe className="w-2.5 h-2.5" /> Platform</> : <><Building2 className="w-2.5 h-2.5" /> Org</>}
                      </span>}
                    </div>
                  </div>
                </div>
                <div>
                  {override ? (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setEditingTab(override)} data-testid={`edit-builtin-${b.key}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Customize
                      </Button>
                      <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={() => deleteTab(override.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCreate({ mode: 'builtin_override', builtin_key: b.key, name: b.name, icon: b.icon })}
                      data-testid={`customize-builtin-${b.key}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Customize
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Custom tabs */}
      <Card className="careiq-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Custom Tabs</CardTitle>
          <p className="text-xs text-[#64748B]">Admin-created tabs with custom fields or linked forms.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {tabs.filter(t => t.type !== 'builtin_override').length === 0 ? (
            <div className="text-center py-8 text-sm text-[#94A3B8]" data-testid="custom-tabs-empty">
              No custom tabs yet. Click "Add Tab" above to create one.
            </div>
          ) : (
            tabs.filter(t => t.type !== 'builtin_override').map((t, i, arr) => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded border border-[#E2E8F0] bg-white" data-testid={`custom-tab-${t.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col">
                    <button onClick={() => moveTab(t.id, 'up')} disabled={i === 0} className="disabled:opacity-30 hover:text-[#0E7490]"><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveTab(t.id, 'down')} disabled={i === arr.length - 1} className="disabled:opacity-30 hover:text-[#0E7490]"><ArrowDown className="w-3 h-3" /></button>
                  </div>
                  <IconFor name={t.icon} className="w-4 h-4 text-[#0E7490]" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0A2342]">{t.name}</div>
                    <div className="text-[11px] text-[#64748B] flex items-center gap-2 flex-wrap">
                      {t.type === 'form_linked' ? (
                        <span className="px-1.5 py-0.5 bg-[#FEF3C7] text-[#92400E] rounded">Form-linked</span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-[#DCFCE7] text-[#166534] rounded">{t.fields?.length || 0} field{(t.fields?.length || 0) === 1 ? '' : 's'}</span>
                      )}
                      {t.hidden && <span className="px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B] rounded">Hidden</span>}
                      <span className="px-1.5 py-0.5 bg-[#F0FDFA] text-[#0E7490] rounded flex items-center gap-1">
                        {t.scope === 'platform' ? <><Globe className="w-2.5 h-2.5" /> Platform</> : <><Building2 className="w-2.5 h-2.5" /> Org</>}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditingTab(t)} data-testid={`edit-${t.id}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={() => deleteTab(t.id)} data-testid={`delete-${t.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {showCreate && (
        <TabEditorDialog
          open={true}
          onClose={() => setShowCreate(false)}
          onSave={createTab}
          forms={forms}
          initial={typeof showCreate === 'object' ? showCreate : null}
        />
      )}
      {editingTab && (
        <TabEditorDialog
          open={true}
          onClose={() => setEditingTab(null)}
          onSave={(payload) => updateTab(editingTab.id, payload)}
          forms={forms}
          initial={editingTab}
          editMode
        />
      )}
    </div>
  );
}

// ---------- Tab editor dialog ----------
function TabEditorDialog({ open, onClose, onSave, forms, initial, editMode }) {
  const [name, setName] = useState(initial?.name || '');
  const [icon, setIcon] = useState(initial?.icon || 'FileText');
  const [type, setType] = useState(initial?.type || initial?.mode || 'custom');
  const [formId, setFormId] = useState(initial?.form_id || '');
  const [builtinKey, setBuiltinKey] = useState(initial?.builtin_key || '');
  const [hidden, setHidden] = useState(initial?.hidden || false);
  const [fields, setFields] = useState(initial?.fields || []);
  const [disabledFields, setDisabledFields] = useState(initial?.disabled_fields || []);
  const [saving, setSaving] = useState(false);
  // PDF import state
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfImportedForm, setPdfImportedForm] = useState(null);  // {id, name, fieldCount, pageCount}

  const isBuiltinOverride = type === 'builtin_override' || !!builtinKey;
  const isFormLinked = type === 'form_linked';
  const isPdfImport = type === 'pdf_import';

  const addField = () => {
    setFields(prev => [...prev, {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label: '',
      type: 'text',
      required: false,
      options: [],
      placeholder: '',
      icon: null,
    }]);
  };

  const addSectionHeader = () => {
    setFields(prev => [...prev, {
      id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label: 'Section',
      type: 'section',
      required: false,
      options: [],
      placeholder: '',
    }]);
  };

  const updateField = (idx, patch) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  };

  const removeField = (idx) => setFields(prev => prev.filter((_, i) => i !== idx));

  const moveField = (idx, dir) => {
    setFields(prev => {
      const next = [...prev];
      const tgt = idx + (dir === 'up' ? -1 : 1);
      if (tgt < 0 || tgt >= next.length) return next;
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Tab name required'); return; }
    if (isFormLinked && !formId) { toast.error('Pick a form to link'); return; }
    if (isPdfImport && !pdfImportedForm) { toast.error('Upload a PDF first'); return; }
    if (isBuiltinOverride && !builtinKey) { toast.error('Builtin key required'); return; }
    // Validate fields
    const cleanFields = fields.map(f => ({
      ...f,
      label: (f.label || '').trim(),
      options: Array.isArray(f.options) ? f.options.filter(o => (o || '').trim()) : [],
    })).filter(f => f.label);

    // PDF-import tabs become form_linked tabs pointing to the just-created form
    const effectiveType = isPdfImport ? 'form_linked' : type;
    const effectiveFormId = isPdfImport ? pdfImportedForm.id : (isFormLinked ? formId : null);

    const payload = {
      name: name.trim(),
      icon,
      type: editMode ? undefined : effectiveType,
      form_id: effectiveFormId,
      builtin_key: isBuiltinOverride ? builtinKey : null,
      hidden,
      fields: (isFormLinked || isPdfImport) ? [] : cleanFields,
      disabled_fields: isBuiltinOverride ? disabledFields : [],
    };
    // Only include non-null values
    Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
    setSaving(true);
    try { await onSave(payload); } finally { setSaving(false); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // 1. AI analyzes PDF → returns form def with bboxes (job-polled)
      const res = await formsApi.aiGeneratePdfLayout(fd);
      const gen = res.data.generated_form;
      // 2. Create the form (published) so it can be linked immediately
      const createRes = await api.post('/forms', {
        name: gen.name || file.name.replace(/\.pdf$/i, ''),
        description: gen.description || '',
        category: gen.category || '',
        status: 'published',
        assigned_roles: [],
        fields: (gen.fields || []).map((f, i) => ({
          field_id: f.id || f.field_id || `f_${i}`,
          label: f.label || `Field ${i + 1}`,
          field_type: f.type || f.field_type || 'text',
          required: !!f.required,
          options: f.options || [],
          order: i,
          page_index: f.page_index,
          bbox: f.bbox,
        })),
        print_layout: 'pdf_original',
        source_pdf_pages: gen.source_pdf_pages || [],
        page_dimensions: gen.page_dimensions || [],
      });
      setPdfImportedForm({
        id: createRes.data.id,
        name: gen.name,
        fieldCount: (gen.fields || []).length,
        pageCount: res.data.page_count || 1,
      });
      if (!name.trim()) setName(gen.name || file.name.replace(/\.pdf$/i, ''));
      toast.success(`PDF imported — ${gen.fields?.length || 0} fields across ${res.data.page_count || 1} page(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PDF import failed');
    } finally {
      setPdfUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tab-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editMode ? 'Edit Tab' : 'Add New Tab'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type selector (create-only) */}
          {!editMode && !isBuiltinOverride && (
            <div>
              <Label className="careiq-label">Tab Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom fields (define your own)</SelectItem>
                  <SelectItem value="form_linked">Link an existing form</SelectItem>
                  <SelectItem value="pdf_import">Import from PDF (layout preserved for print)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {isBuiltinOverride && (
            <div className="p-3 rounded bg-[#FEF3C7] text-[#92400E] text-xs">
              Customizing built-in tab: <strong>{builtinKey}</strong>. Fields you add here will appear below the native fields on the {builtinKey} tab.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="careiq-label">Tab Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Insurance Info" className="careiq-input" data-testid="tab-name-input" />
            </div>
            <div>
              <Label className="careiq-label">Icon</Label>
              <Select value={icon} onValueChange={setIcon}>
                <SelectTrigger data-testid="icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(ICONS).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isFormLinked && (
            <div>
              <Label className="careiq-label">Linked Form</Label>
              <Select value={formId} onValueChange={setFormId}>
                <SelectTrigger data-testid="form-select"><SelectValue placeholder="Pick a published form" /></SelectTrigger>
                <SelectContent>
                  {forms.length === 0 && <div className="p-2 text-xs text-[#94A3B8]">No published forms found</div>}
                  {forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {formId && (
                <a
                  href={`/forms/builder?id=${formId}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#0E7490] hover:text-[#0C6580] underline"
                  data-testid="open-form-builder-link"
                >
                  <Pencil className="w-3 h-3" />
                  Edit fields of the linked form
                </a>
              )}
            </div>
          )}

          {isPdfImport && (
            <div data-testid="pdf-import-section">
              <Label className="careiq-label">Upload PDF Form</Label>
              {pdfImportedForm ? (
                <div className="p-4 rounded-lg border border-[#0E7490] bg-[#F0FDFA]" data-testid="pdf-imported-summary">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#0A2342]">✓ {pdfImportedForm.name}</div>
                      <div className="text-xs text-[#64748B]">
                        {pdfImportedForm.fieldCount} fields extracted across {pdfImportedForm.pageCount} page(s) — layout preserved for print
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setPdfImportedForm(null)}>Re-upload</Button>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-[#E2E8F0] rounded-xl cursor-pointer hover:border-[#0E7490] transition-colors" data-testid="pdf-upload-label">
                  {pdfUploading ? (
                    <><Loader2 className="w-7 h-7 text-[#0E7490] animate-spin mb-2" /><span className="text-sm text-[#0E7490]">Extracting fields + layout (~30s)...</span></>
                  ) : (
                    <><Plus className="w-7 h-7 text-[#94A3B8] mb-2" /><span className="text-sm text-[#64748B]">Click to upload PDF</span><span className="text-xs text-[#94A3B8] mt-1">Max 10 pages · Filled submissions print pixel-perfect to original</span></>
                  )}
                  <input type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} disabled={pdfUploading} data-testid="pdf-file-input" />
                </label>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch checked={hidden} onCheckedChange={setHidden} data-testid="hidden-switch" />
            <Label className="text-sm cursor-pointer">Hide this tab from patient records</Label>
          </div>

          {isBuiltinOverride && builtinKey && BUILTIN_TAB_FIELD_REGISTRY[builtinKey] && (
            <div className="border-t pt-4" data-testid="builtin-fields-toggle-section">
              <div className="mb-2">
                <Label className="text-base font-semibold text-[#0A2342]">Built-in Fields</Label>
                <p className="text-xs text-[#64748B]">Toggle off any built-in fields you don't want shown on this tab for patients.</p>
              </div>
              <div className="space-y-3">
                {BUILTIN_TAB_FIELD_REGISTRY[builtinKey].groups.map((grp, gi) => (
                  <div key={gi} className="bg-[#F8FAFC] rounded-lg p-2.5 border border-[#E2E8F0]">
                    <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">{grp.title}</div>
                    <div className="grid md:grid-cols-2 gap-1.5">
                      {grp.fields.map(f => {
                        const disabled = disabledFields.includes(f.key);
                        return (
                          <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-white" data-testid={`builtin-field-toggle-${f.key}`}>
                            <input
                              type="checkbox"
                              checked={!disabled}
                              onChange={(e) => {
                                if (e.target.checked) setDisabledFields(prev => prev.filter(k => k !== f.key));
                                else setDisabledFields(prev => [...prev, f.key]);
                              }}
                            />
                            <span className={disabled ? 'text-[#94A3B8] line-through' : 'text-[#0A2342]'}>{f.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isFormLinked && !isPdfImport && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base font-semibold text-[#0A2342]">Fields</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={addSectionHeader} data-testid="add-section-btn">
                    <Plus className="w-3 h-3 mr-1" /> Section Header
                  </Button>
                  <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Field
                  </Button>
                </div>
              </div>
              {fields.length === 0 ? (
                <div className="text-center py-6 text-sm text-[#94A3B8] border border-dashed rounded">No fields yet. Click "Add Field".</div>
              ) : (
                <div className="space-y-3">
                  {fields.map((f, idx) => (
                    <FieldRow
                      key={f.id}
                      field={f}
                      idx={idx}
                      isFirst={idx === 0}
                      isLast={idx === fields.length - 1}
                      onChange={(patch) => updateField(idx, patch)}
                      onRemove={() => removeField(idx)}
                      onMove={(dir) => moveField(idx, dir)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="careiq-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-tab-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {editMode ? 'Save Changes' : 'Create Tab'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ field, idx, isFirst, isLast, onChange, onRemove, onMove }) {
  const needsOptions = field.type === 'select';
  const isSection = field.type === 'section';
  return (
    <div className={`p-3 rounded border ${isSection ? 'border-[#0E7490] bg-[#F0FDFA]' : 'border-[#E2E8F0] bg-[#FAFBFC]'} space-y-2`} data-testid={`field-row-${idx}`}>
      <div className="grid md:grid-cols-6 gap-2 items-end">
        <div className="md:col-span-2">
          <Label className="careiq-label text-[10px]">{isSection ? 'Section Title' : 'Label'}</Label>
          <Input value={field.label} onChange={e => onChange({ label: e.target.value })} placeholder={isSection ? 'e.g., Insurance Info' : 'e.g., Member ID'} className="careiq-input h-9" data-testid={`field-label-${idx}`} />
        </div>
        <div>
          <Label className="careiq-label text-[10px]">Type</Label>
          <Select value={field.type} onValueChange={(v) => onChange({ type: v })}>
            <SelectTrigger className="h-9" data-testid={`field-type-${idx}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!isSection && (
          <div>
            <Label className="careiq-label text-[10px]">Icon</Label>
            <Select value={field.icon || 'none'} onValueChange={(v) => onChange({ icon: v === 'none' ? null : v })}>
              <SelectTrigger className="h-9" data-testid={`field-icon-${idx}`}>
                <SelectValue>
                  {field.icon && ICONS[field.icon] ? (
                    <span className="flex items-center gap-1.5"><IconFor name={field.icon} className="w-3.5 h-3.5" /><span className="text-xs">{field.icon}</span></span>
                  ) : <span className="text-xs text-[#94A3B8]">None</span>}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="none"><span className="text-xs text-[#94A3B8]">None</span></SelectItem>
                {Object.keys(ICONS).map(name => (
                  <SelectItem key={name} value={name}>
                    <span className="flex items-center gap-2"><IconFor name={name} className="w-3.5 h-3.5" /><span className="text-xs">{name}</span></span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {!isSection && (
          <div className="flex items-center gap-1 pt-5">
            <Switch checked={field.required} onCheckedChange={(v) => onChange({ required: v })} />
            <span className="text-xs text-[#64748B]">Required</span>
          </div>
        )}
        <div className={`flex justify-end gap-1 ${isSection ? 'md:col-span-3' : ''}`}>
          <Button size="sm" variant="ghost" disabled={isFirst} onClick={() => onMove('up')}><ArrowUp className="w-3 h-3" /></Button>
          <Button size="sm" variant="ghost" disabled={isLast} onClick={() => onMove('down')}><ArrowDown className="w-3 h-3" /></Button>
          <Button size="sm" variant="ghost" className="text-[#991B1B]" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      {needsOptions && (
        <div>
          <Label className="careiq-label text-[10px]">Options (one per line)</Label>
          <textarea
            value={(field.options || []).join('\n')}
            onChange={e => onChange({ options: e.target.value.split('\n') })}
            className="w-full careiq-input min-h-[70px] text-xs font-mono"
            placeholder={'Option A\nOption B\nOption C'}
            data-testid={`field-options-${idx}`}
          />
        </div>
      )}
      <div>
        <Label className="careiq-label text-[10px]">Placeholder / help text (optional)</Label>
        <Input value={field.placeholder || ''} onChange={e => onChange({ placeholder: e.target.value })} className="careiq-input h-8 text-xs" />
      </div>
    </div>
  );
}
